//mapaddaddress

import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip, useMapEvents, useMap, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon from '../assets/icons/RedMap.svg';
import markerIconRetina from '../assets/icons/RedMap.svg';
import 'leaflet.locatecontrol/dist/L.Control.Locate.min.css'; // Import styles for LocateControl
import { LocateControl } from 'leaflet.locatecontrol'; // Import LocateControl
import { useLocation } from 'react-router-dom';

const defaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIconRetina,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

interface MapAddAddressProps {
  onLocationChange: (lat: string, lon: string, pinCode: string, street: string, city: string) => void;

}

const SearchButton: React.FC<{ onSearch: (lat: number, lng: number) => void }> = ({ onSearch }) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [suggestions, setSuggestions] = useState<any[]>([]);  // State for suggestions

  const handleSearch = () => {
    if (!searchQuery) return;

    // Fetch coordinates from OpenStreetMap Nominatim API based on search query, limited to Kerala
    fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=json&bounded=1&viewbox=74.0850,12.7653,77.2616,8.1811`)
      .then(response => response.json())
      .then(data => {
        if (data.length > 0) {
          const { lat, lon } = data[0];
          onSearch(parseFloat(lat), parseFloat(lon));
        } else {
          alert('Location not found');
        }
      })
      .catch(error => console.error('Error fetching search results:', error));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);

    if (e.target.value) {
      // Fetch suggestions from OpenStreetMap API as user types, limited to Kerala
      fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(e.target.value)}&format=json&bounded=1&viewbox=74.0850,12.7653,77.2616,8.1811`)
        .then(response => response.json())
        .then(data => {
          setSuggestions(data); // Store suggestions
        })
        .catch(error => console.error('Error fetching suggestions:', error));
    } else {
      setSuggestions([]); // Clear suggestions if input is empty
    }
  };

  const handleSuggestionClick = (lat: number, lon: number) => {
    onSearch(lat, lon);  // Call onSearch with the selected location's coordinates
    setSearchQuery('');   // Clear the input field
    setSuggestions([]);  // Clear suggestions
  };


  return (
    <div className="search-container absolute -top-24 right-0 flex">
      <input
        type="text"
        value={searchQuery}
        onChange={handleInputChange}
        placeholder="Search location"
        className="flex-grow pl-4 pr-2 py-2 rounded-md focus:outline-none border-none placeholder-gray-500 w-full sm:w-auto"

      />
      <button onClick={handleSearch} className=" text-white rounded ml-2 px-4 py-2 bg-blue-600">
        <svg
          className="w-5 h-5 mr-2"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {suggestions.length > 0 && (
        <ul className="absolute bg-white shadow-lg border mt-12 w-full z-50 max-h-60 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <li
              key={index}
              className="px-4 py-2 cursor-pointer hover:bg-gray-200"
              onClick={() => handleSuggestionClick(parseFloat(suggestion.lat), parseFloat(suggestion.lon))}
            >
              {suggestion.display_name}
            </li>
          ))}
        </ul>
      )}



    </div>
  );
};
const LocateControlComponent = () => {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    const locateOptions = {
      position: 'topright' as L.ControlPosition,
      strings: {
        title: 'Show me where I am!',
      },
      locateOptions: {
        enableHighAccuracy: true,
        maxZoom: 16,
      },
    };

    const locateControl = new LocateControl(locateOptions).addTo(map);
    locateControl.start();

    return () => {
      locateControl.stop();
      map.removeControl(locateControl);
    };
  }, [map]);

  return null;
};

const MapAddAddress: React.FC<MapAddAddressProps> = ({ onLocationChange }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const latitude = queryParams.get('latitude');
  const longitude = queryParams.get('longitude');
  const [markerPosition, setMarkerPosition] = useState<[number, number]>([9.9921, 76.3019]);
  const [pincode, setPincode] = useState<string>('');
  const [street, setStreet] = useState<string>('');
  const [city, setCity] = useState<string>('');

  useEffect(() => {
    if (latitude && longitude) {
      updateLocationDetails(parseFloat(latitude), parseFloat(longitude));
    }
  }, [latitude, longitude]);

  const updateLocationDetails = (lat: number, lng: number) => {
    setMarkerPosition([lat, lng]);

    // Fetch street, city, and pincode based on lat/lon using OpenStreetMap Nominatim API
    fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`)
      .then(response => response.json())
      .then(data => {
        const pinCode = data.address?.postcode || 'N/A';
        const street = data.address?.road || 'N/A';
        const city = data.address?.city || data.address?.town || data.address?.village || 'N/A';

        setPincode(pinCode);
        setStreet(street);
        setCity(city);

        onLocationChange(lat.toString(), lng.toString(), pinCode, street, city);
      })
      .catch(error => console.error('Error fetching location details:', error));
  };

  const MapClickHandler = () => {
    useMapEvents({
      click(e) {
        const { lat, lng } = e.latlng;
        updateLocationDetails(lat, lng);
      },
    });
    return null;
  };

  const MapMover: React.FC<{ center: [number, number] }> = ({ center }) => {
    const map = useMap();
    map.setView(center, map.getZoom());
    return null;
  };

  return (
    <div className="relative">
      <MapContainer center={markerPosition} zoom={13} className="h-[400px] w-full z-10">
        <LayersControl position="topright">
          <LayersControl.BaseLayer name="Default Map" checked>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Satellite">
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution='&copy; Esri, Maxar, Earthstar Geographics, and the GIS User Community'
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        <Marker position={markerPosition} icon={defaultIcon}>
          {/* Only show tooltip if we have street, city, and pincode */}
          {(street || city || pincode) && (
            <Tooltip direction="top" offset={[0, -10]} opacity={1} permanent>
              <span>{street}, {city}, {pincode}</span>
            </Tooltip>
          )}
        </Marker>

        <MapClickHandler />
        <MapMover center={markerPosition} />
        <LocateControlComponent />
      </MapContainer>

      <SearchButton onSearch={(lat, lng) => updateLocationDetails(lat, lng)} />
    </div>
  );
};

export default MapAddAddress;
